/**
 * Catalog modals — edit catalog, edit captions, share links.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent,
  Box, Typography, IconButton, Button, TextField,
  CircularProgress, Alert, Select, MenuItem, FormControl
} from '@mui/material';
import { Close } from '@mui/icons-material';
import merchantAPI, { catalogAPI } from '../../../services/merchantAPI';
import FlutterServicesAPI from '../../../services/flutterServices';
import { API_URLS } from '../../../config/apiConfig';
import { catalogColors, catalogFonts, underlineFieldSx, CATALOG_NAME_MAX_LENGTH } from './catalogTheme';


// ---------------------------------------------------------------------------
// Edit catalog
// ---------------------------------------------------------------------------
export function CreateEditCatalogDialog({ open, catalog, onClose, onUpdated, onDeleteClick }) {
  const [name, setName] = useState('');
  const [description, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');

  useEffect(() => {
    if (open) {
      merchantAPI.getBrandLogos()
        .then(res => setBrands(res.data.brand_logos || []))
        .catch(console.error);
    }
  }, [open]);

  useEffect(() => {
    if (open && catalog) {
      setName(catalog.name || '');
      setDesc(catalog.description || '');
      setSelectedBrand(catalog.brand_id || '');
    }
    setError('');
  }, [open, catalog]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Catalog name is required');
      return;
    }
    if (trimmedName.length > CATALOG_NAME_MAX_LENGTH) {
      setError(`Catalog name must be ${CATALOG_NAME_MAX_LENGTH} characters or less`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await catalogAPI.updateCatalog(catalog.id, {
        name: trimmedName,
        description: description.trim() || '',
        brand_id: selectedBrand || null,
      });
      onUpdated(res.data.catalog);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save catalog');
    } finally {
      setSaving(false);
    }
  };

  if (!catalog) return null;

  return (
    <Dialog
      className="edit-catalog-dialog"
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: '#FFFFFF',
          boxShadow: 'none',
          width: { xs: 'calc(100% - 22px)', sm: '100%' },
          m: { xs: '11px auto', sm: '32px auto' },
        }
      }}
    >
      <Box
        sx={{
          m: 2.5,
          p: 2,
          border: '1px solid rgba(204, 195, 216, 0.3)',
          borderRadius: '12px',
          position: 'relative',
        }}
      >
        {/* Close button at top-right of the inner border box */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: -15,
            padding: "5px",
            right: -10,
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: '#F4F2F7',
            '&:hover': { bgcolor: '#EBE9ED' }
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 24 24" fill="none">
            <path d="M7.75702 16.243L16.243 7.75696M16.243 16.243L7.75702 7.75696" stroke="black" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>

        {/* Title */}
        <Typography
          className="edit-catalog-dialog-title"
          sx={{
            fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
            fontWeight: 700,
            fontSize: { xs: 16, md: 20 },
            color: catalogColors.textPrimary,
            mb: 3
          }}
        >
          Edit Catalog
        </Typography>

        {error && <Typography sx={{ color: '#E53935', fontSize: 13, mb: 1.5 }}>{error}</Typography>}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography className="edit-catalog-dialog-name-label" sx={{ fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif', fontSize: { xs: 13, md: 14 }, color: '#7B7487', mb: 0.5 }}>
              Catalog Name
            </Typography>
            <TextField
              className="edit-catalog-dialog-name-input"
              variant="standard"
              value={name}
              onChange={e => setName(e.target.value.slice(0, CATALOG_NAME_MAX_LENGTH))}
              fullWidth
              autoFocus
              inputProps={{ maxLength: CATALOG_NAME_MAX_LENGTH }}
              InputProps={{ disableUnderline: true }}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: 14, md: 16 },
                  color: '#191C1D',
                  borderBottom: '2px solid #191C1D',
                  pb: 0
                }
              }}
            />
          </Box>

          <Box>
            <Typography className="edit-catalog-dialog-desc-label" sx={{ fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif', fontSize: 14, color: '#7B7487', mb: 0.5 }}>
              Catalog Description
            </Typography>
            <TextField
              className="edit-catalog-dialog-desc-input"
              variant="standard"
              value={description}
              onChange={e => setDesc(e.target.value)}
              fullWidth
              multiline
              rows={2}
              InputProps={{ disableUnderline: true }}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: 14, md: 16 },
                  color: '#191C1D',
                  borderBottom: '2px solid #191C1D',
                  pb: 0
                }
              }}
            />
          </Box>


          <Box>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif', fontSize: { xs: 13, md: 14 }, color: '#7B7487', mb: 0.5 }}>
              Select Your Brand
            </Typography>
            <Select
              variant="standard"
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              displayEmpty
              fullWidth
              disableUnderline
              MenuProps={{ disablePortal: true }}
              sx={{
                '& .MuiSelect-select': {
                  fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: 14, md: 16 },
                  color: selectedBrand ? '#191C1D' : '#9B8FAE',
                  borderBottom: '2px solid #191C1D',
                  pb: 0.5,
                  pl: 0,
                  bgcolor: 'transparent'
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select Your Brand</em>
              </MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>{brand.brand_name || brand.name}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              bgcolor: catalogColors.purple,
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': { bgcolor: catalogColors.purpleDark }
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit captions
// ---------------------------------------------------------------------------
export function EditCaptionDialog({ open, item, catalogId, onClose, onUpdated }) {
  const [caption1, setCaption1] = useState('');
  const [caption2, setCaption2] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && item) {
      setCaption1(item.caption_1 !== 'NA' ? item.caption_1 : '');
      setCaption2(item.caption_2 !== 'NA' ? item.caption_2 : '');
    }
    setError('');
  }, [open, item]);

  const handleSave = async () => {
    if (!item) return;
    setSaving(true);
    try {
      const res = await catalogAPI.updateItem(catalogId, item.id, {
        caption_1: caption1.trim() || null,
        caption_2: caption2.trim() || null,
      });
      onUpdated(res.data.item);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save captions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, width: { xs: 'calc(100% - 22px)', sm: '100%' }, m: { xs: '11px auto', sm: '32px auto' } } }}>
      <Box sx={{ px: 3, pt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontFamily: catalogFonts.semibold, fontWeight: 700, fontSize: 18 }}>Edit Product</Typography>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </Box>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        {error && <Typography sx={{ color: '#E53935', fontSize: 13, mb: 1 }}>{error}</Typography>}
        <Typography sx={{ fontSize: 13, color: catalogColors.textSecondary, mb: 0.5 }}>Caption 1</Typography>
        <TextField variant="standard" value={caption1} onChange={e => setCaption1(e.target.value)} fullWidth placeholder='Leave blank for "NA"' sx={{ ...underlineFieldSx, mb: 2.5 }} />
        <Typography sx={{ fontSize: 13, color: catalogColors.textSecondary, mb: 0.5 }}>Caption 2</Typography>
        <TextField variant="standard" value={caption2} onChange={e => setCaption2(e.target.value)} fullWidth placeholder='Leave blank for "NA"' sx={underlineFieldSx} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} sx={{ bgcolor: catalogColors.purple, color: '#fff', borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: catalogColors.purpleDark } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Share links
// ---------------------------------------------------------------------------
function buildShareUrl(token) {
  return `${window.location.origin}/catalog/${token}`;
}

export function ShareLinkDialog({ open, catalog, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [error, setError] = useState('');

  const loadLinks = useCallback(async () => {
    if (!catalog) return;
    setLoading(true);
    try {
      const res = await catalogAPI.getShareLinks(catalog.id);
      setLinks(res.data.share_links || []);
    } catch {
      setError('Failed to load share links');
    } finally {
      setLoading(false);
    }
  }, [catalog]);

  useEffect(() => {
    if (open) {
      setCopyFeedback(false);
      setError('');
      loadLinks();
    }
  }, [open, loadLinks]);

  const activeLink = links.find(l => !l.is_expired && l.is_active);
  const shareUrl = activeLink ? buildShareUrl(activeLink.token) : null;

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 1800);
    });
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        const result = await FlutterServicesAPI.shareCatalogSocial(catalog, shareUrl);
        if (result?.success === false) {
          setError(result.message || 'Share failed');
        }
      } catch (err) {
        setError(err?.message || 'Share failed. Please try again.');
      }
      return;
    }
    const { title, shareMessage } = FlutterServicesAPI.buildCatalogSharePayload(catalog, shareUrl);
    if (navigator.share) {
      navigator.share({ title, text: shareMessage }).catch(() => { });
    } else {
      handleCopy();
    }
  };

  const handleWhatsApp = async () => {
    if (!shareUrl) return;
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        const result = await FlutterServicesAPI.shareWhatsappCatalog(catalog, shareUrl);
        if (result?.success === false) {
          setError(result.message || 'WhatsApp share failed');
        }
      } catch (err) {
        setError(err?.message || 'WhatsApp share failed. Please try again.');
      }
      return;
    }
    const { shareMessage } = FlutterServicesAPI.buildCatalogSharePayload(catalog, shareUrl);

    // Share URL as text only — WhatsApp fetches OG tags from the URL and shows
    // the rich preview card (title + description + catalog image) automatically.
    try {
      if (navigator.share) {
        await navigator.share({ text: shareMessage });
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }

    const text = encodeURIComponent(shareMessage);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleEmail = async () => {
    if (!shareUrl) return;
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        const result = await FlutterServicesAPI.shareMailCatalog(catalog, shareUrl);
        if (result?.success === false) {
          setError(result.message || 'Email share failed');
        }
      } catch (err) {
        setError(err?.message || 'Email share failed. Please try again.');
      }
      return;
    }
    const { subject, shareMessage } = FlutterServicesAPI.buildCatalogSharePayload(catalog, shareUrl);
    window.open(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareMessage)}`,
      '_blank',
    );
  };

  const handleGetLink = async () => {
    setCreating(true);
    try {
      const res = await catalogAPI.createShareLink(catalog.id, { label: 'Share Link' });
      setLinks(prev => [res.data.share_link, ...prev]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          bgcolor: '#FFFFFF',
          boxShadow: '0px 24px 64px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          width: { xs: 'calc(100% - 22px)', sm: '100%' },
          m: { xs: '11px auto', sm: '32px auto' },
        },
      }}
    >
      <Box sx={{ p: 3, pb: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
              fontWeight: 700,
              fontSize: '18px',
              color: '#191C1D',
            }}
          >
            Share Catalog
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: '#7B7487', '&:hover': { bgcolor: '#F4F2F7' } }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* URL copy box */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} sx={{ color: '#7C3AED' }} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                border: '1.5px dashed #CCC3D8',
                borderRadius: '12px',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                cursor: shareUrl ? 'pointer' : 'default',
              }}
              onClick={handleCopy}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {shareUrl ? (
                  <>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#191C1D',
                      }}
                    >
                      {shareUrl}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
                        fontSize: '13px',
                        color: copyFeedback ? '#4CAF50' : '#9B8FAE',
                        mt: 0.25,
                      }}
                    >
                      {copyFeedback ? '✓ Copied!' : 'Tap to Copy'}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
                      fontSize: '13px',
                      color: '#9B8FAE',
                    }}
                  >
                    No active link — generate one below
                  </Typography>
                )}
              </Box>

              {/* Copy icon */}
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                disabled={!shareUrl}
                sx={{
                  color: copyFeedback ? '#4CAF50' : '#4A4455',
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#F4F2F7' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                </svg>
              </IconButton>

              {/* Native share icon */}
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleNativeShare(); }}
                disabled={!shareUrl}
                sx={{
                  color: '#4A4455',
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#F4F2F7' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </IconButton>
            </Box>

            {/* Share via options */}
            <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 1 }}>
              {/* WhatsApp */}
              <Box
                onClick={handleWhatsApp}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  cursor: shareUrl ? 'pointer' : 'not-allowed',
                  opacity: shareUrl ? 1 : 0.45,
                  minWidth: 72,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: '#25D366',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(37,211,102,0.3)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': shareUrl ? { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(37,211,102,0.4)' } : {},
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#4A4455',
                    textAlign: 'center',
                  }}
                >
                  WhatsApp
                </Typography>
              </Box>

              {/* Email */}
              <Box
                onClick={handleEmail}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  cursor: shareUrl ? 'pointer' : 'not-allowed',
                  opacity: shareUrl ? 1 : 0.45,
                  minWidth: 72,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: '#4F6BF5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(79,107,245,0.3)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': shareUrl ? { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(79,107,245,0.4)' } : {},
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M2 7l10 7 10-7" />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#4A4455',
                    textAlign: 'center',
                  }}
                >
                  Email
                </Typography>
              </Box>

              {/* PDF */}
              <Box
                onClick={async () => {
                  if (!activeLink?.token) return;
                  try {
                    const result = await FlutterServicesAPI.downloadCatalogPdf(
                      activeLink.token,
                      catalog?.name || ''
                    );
                    if (result?.platform === 'web' && result?.blobUrl) {
                      const a = document.createElement('a');
                      a.href = result.blobUrl;
                      a.download = result.fileName || 'catalogue.pdf';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(result.blobUrl);
                    } else if (result?.success === false) {
                      setError(result.message || 'PDF download failed');
                    }
                  } catch (err) {
                    setError(err?.message || 'PDF download failed. Please try again.');
                  }
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  cursor: shareUrl ? 'pointer' : 'not-allowed',
                  opacity: shareUrl ? 1 : 0.45,
                  minWidth: 72,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: '#E8450A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(232,69,10,0.3)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': shareUrl ? { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(232,69,10,0.4)' } : {},
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="9" y1="13" x2="15" y2="13" />
                    <line x1="9" y1="17" x2="12" y2="17" />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#4A4455',
                    textAlign: 'center',
                  }}
                >
                  PDF
                </Typography>
              </Box>
            </Box>

            {/* Generate link CTA if no active link */}
            {!shareUrl && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  onClick={handleGetLink}
                  disabled={creating}
                  sx={{
                    bgcolor: '#7C3AED',
                    color: '#fff',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': { bgcolor: '#6D28D9' },
                  }}
                >
                  {creating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Generate Share Link'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
}
